import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ApiClient, createAuthApi } from '@shared/api';
const generateAvatarUrl = (name) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`;
};
const seedUsers = [
    {
        id: '1',
        username: 'admin',
        displayName: 'Admin User',
        email: 'admin@projectmanmd.com',
        avatarUrl: generateAvatarUrl('Admin User'),
        role: 'admin',
        password: 'admin123',
        active: true,
    },
    {
        id: '2',
        username: 'sarah',
        displayName: 'Sarah Manager',
        email: 'sarah@projectmanmd.com',
        avatarUrl: generateAvatarUrl('Sarah Manager'),
        role: 'manager',
        password: 'sarah123',
        active: true,
    },
    {
        id: '3',
        username: 'maya',
        displayName: 'Maya Johnson',
        email: 'maya@projectmanmd.com',
        avatarUrl: generateAvatarUrl('Maya Johnson'),
        role: 'developer',
        password: 'maya123',
        active: true,
    },
    {
        id: '4',
        username: 'alex',
        displayName: 'Alex Kim',
        email: 'alex@projectmanmd.com',
        avatarUrl: generateAvatarUrl('Alex Kim'),
        role: 'developer',
        password: 'alex123',
        active: true,
    },
    {
        id: '5',
        username: 'carlos',
        displayName: 'Carlos Rodriguez',
        email: 'carlos@projectmanmd.com',
        avatarUrl: generateAvatarUrl('Carlos Rodriguez'),
        role: 'developer',
        password: 'carlos123',
        active: true,
    },
];
export const useAuthStore = create()(persist((set, get) => ({
    user: null,
    isAuthenticated: false,
    users: seedUsers,
    isLoading: false,
    error: null,
    serverUrl: null,
    accessToken: null,
    refreshToken: null,
    setServerUrl: (url) => {
        set({ serverUrl: url.replace(/\/+$/, '') });
    },
    login: async (username, password) => {
        const { serverUrl, users } = get();
        // Ensure seed users exist if users array is empty
        const availableUsers = users.length > 0 ? users : seedUsers;
        if (!serverUrl) {
            const user = availableUsers.find((u) => u.username === username && u.password === password);
            if (user) {
                set({ user: user, isAuthenticated: true, error: null, users: availableUsers });
                return true;
            }
            set({ error: 'Invalid username or password' });
            return false;
        }
        set({ isLoading: true, error: null });
        try {
            const client = new ApiClient(serverUrl);
            const authApi = createAuthApi(client);
            const res = await authApi.login(username, password);
            client.setTokens(res.accessToken, res.refreshToken);
            const { users } = get();
            const existingUser = users.find((u) => u.id === res.user.id || u.username === username);
            const apiUser = {
                id: res.user.id,
                username: res.user.username,
                displayName: res.user.displayName,
                email: res.user.email || `${res.user.username}@localhost`,
                avatarUrl: res.user.avatarUrl || existingUser?.avatarUrl || generateAvatarUrl(res.user.displayName),
                role: existingUser?.role || res.user.role || 'developer',
                password: '',
                active: true,
            };
            const mergedUsers = users.some((u) => u.id === apiUser.id)
                ? users.map((u) => (u.id === apiUser.id ? apiUser : u))
                : [...users, apiUser];
            set({
                user: apiUser,
                users: mergedUsers,
                accessToken: res.accessToken,
                refreshToken: res.refreshToken,
                isAuthenticated: true,
                isLoading: false,
            });
            get().fetchUsers();
            return true;
        }
        catch {
            const localUser = users.find((u) => u.username === username && u.password === password);
            if (localUser) {
                try {
                    const client = new ApiClient(serverUrl);
                    const authApi = createAuthApi(client);
                    const res = await authApi.register(username, password, localUser.displayName);
                    client.setTokens(res.accessToken, res.refreshToken);
                    set({
                        user: localUser,
                        accessToken: res.accessToken,
                        refreshToken: res.refreshToken,
                        isAuthenticated: true,
                        error: null,
                        isLoading: false,
                    });
                    get().fetchUsers();
                    return true;
                }
                catch {
                    // registration failed — use local auth without API tokens
                }
                set({ user: localUser, isAuthenticated: true, error: null, isLoading: false });
                return true;
            }
            set({ error: 'Invalid username or password', isLoading: false });
            return false;
        }
    },
    register: async (data) => {
        const { serverUrl, users } = get();
        if (!serverUrl) {
            if (users.some((u) => u.username === data.username)) {
                set({ error: 'Username already taken' });
                return false;
            }
            const newUser = {
                id: Date.now().toString(),
                ...data,
                avatarUrl: generateAvatarUrl(data.displayName),
                active: true,
            };
            set({ users: [...users, newUser] });
            return true;
        }
        set({ isLoading: true, error: null });
        try {
            const client = new ApiClient(serverUrl);
            const authApi = createAuthApi(client);
            const res = await authApi.register(data.username, data.password, data.displayName, data.email, data.role);
            client.setTokens(res.accessToken, res.refreshToken);
            const apiUser = {
                id: res.user.id,
                username: res.user.username,
                displayName: res.user.displayName,
                email: res.user.email || `${res.user.username}@localhost`,
                avatarUrl: res.user.avatarUrl || generateAvatarUrl(res.user.displayName),
                role: res.user.role || 'developer',
                password: '',
                active: true,
            };
            const { users } = get();
            const mergedUsers = users.some((u) => u.id === apiUser.id)
                ? users.map((u) => (u.id === apiUser.id ? apiUser : u))
                : [...users, apiUser];
            set({
                user: apiUser,
                users: mergedUsers,
                accessToken: res.accessToken,
                refreshToken: res.refreshToken,
                isAuthenticated: true,
                isLoading: false,
            });
            get().fetchUsers();
            return true;
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : 'Registration failed';
            set({ error: msg, isLoading: false });
            return false;
        }
    },
    logout: async () => {
        const { serverUrl } = get();
        if (serverUrl) {
            try {
                const client = get().getClient();
                if (client) {
                    const authApi = createAuthApi(client);
                    await authApi.logout();
                }
            }
            catch { /* ignore */ }
        }
        set({ user: null, isAuthenticated: false, error: null, accessToken: null, refreshToken: null });
    },
    updateProfile: async (updates) => {
        const { user, users, serverUrl, getClient } = get();
        if (!user)
            return;
        const updatedUser = { ...user, ...updates };
        if (serverUrl) {
            try {
                const client = getClient();
                if (client) {
                    const authApi = createAuthApi(client);
                    const res = await authApi.updateProfile({
                        displayName: updates.displayName,
                        avatarUrl: updates.avatarUrl,
                    });
                    // Use server response
                    const serverUser = {
                        ...updatedUser,
                        avatarUrl: res.avatarUrl || updatedUser.avatarUrl,
                    };
                    set({
                        user: serverUser,
                        users: users.map((u) => (u.id === user.id ? serverUser : u)),
                    });
                    return;
                }
            }
            catch {
                // Fall through to local update
            }
        }
        // Local-only update (no server or API failed)
        set({
            user: updatedUser,
            users: users.map((u) => (u.id === user.id ? updatedUser : u)),
        });
    },
    fetchUsers: async () => {
        const { serverUrl, getClient } = get();
        if (!serverUrl)
            return;
        try {
            const client = getClient();
            if (!client)
                return;
            const authApi = createAuthApi(client);
            const apiUsers = await authApi.listUsers();
            const { users } = get();
            const mergedUsers = apiUsers.map((apiUser) => {
                const existing = users.find((u) => u.id === apiUser.id);
                return {
                    id: apiUser.id,
                    username: apiUser.username,
                    displayName: apiUser.displayName,
                    email: apiUser.email || existing?.email || `${apiUser.username}@localhost`,
                    avatarUrl: apiUser.avatarUrl || existing?.avatarUrl || generateAvatarUrl(apiUser.displayName),
                    role: apiUser.role || 'developer',
                    password: '',
                    active: apiUser.active,
                };
            });
            set({ users: mergedUsers });
        }
        catch {
            // ignore — keep local users
        }
    },
    getUserById: (id) => {
        return get().users.find((u) => u.id === id);
    },
    getTeamMembers: () => {
        return get().users.map(({ password, ...user }) => user);
    },
    addUser: async (data) => {
        const { users, serverUrl } = get();
        if (serverUrl) {
            try {
                const client = get().getClient();
                if (client) {
                    const authApi = createAuthApi(client);
                    const serverUsers = await authApi.listUsers();
                    if (serverUsers.some((u) => u.username === data.username)) {
                        return false;
                    }
                    const res = await authApi.register(data.username, data.password, data.displayName, data.email, data.role);
                    const newUser = {
                        id: res.user.id,
                        username: res.user.username,
                        displayName: res.user.displayName,
                        email: data.email,
                        avatarUrl: res.user.avatarUrl || generateAvatarUrl(data.displayName),
                        role: res.user.role || data.role,
                        password: '',
                        active: true,
                    };
                    const { users: currentUsers } = get();
                    set({ users: [...currentUsers, newUser] });
                    return true;
                }
            }
            catch {
                // Server unavailable — fall back to local only
            }
        }
        if (users.some((u) => u.username === data.username))
            return false;
        const newUser = {
            id: Date.now().toString(),
            ...data,
            avatarUrl: generateAvatarUrl(data.displayName),
            active: true,
        };
        set({ users: [...users, newUser] });
        return true;
    },
    removeUser: async (id) => {
        const { users, user, serverUrl } = get();
        if (user?.id === id)
            return false;
        if (serverUrl) {
            try {
                const client = get().getClient();
                if (client) {
                    const authApi = createAuthApi(client);
                    await authApi.deleteUser(id);
                }
            }
            catch {
                // Server unavailable — still remove locally
            }
        }
        set({ users: users.filter((u) => u.id !== id) });
        return true;
    },
    updateUser: async (id, updates) => {
        const { users, user, serverUrl } = get();
        if (serverUrl) {
            try {
                const client = get().getClient();
                if (client) {
                    const authApi = createAuthApi(client);
                    const apiUser = await authApi.updateUser(id, {
                        displayName: updates.displayName,
                        email: updates.email,
                        role: updates.role,
                        password: updates.password,
                        avatarUrl: updates.avatarUrl,
                    });
                    const mergedUpdates = {
                        displayName: apiUser.displayName || updates.displayName,
                        email: apiUser.email || updates.email,
                        role: apiUser.role || updates.role,
                        avatarUrl: apiUser.avatarUrl || updates.avatarUrl,
                    };
                    const updated = users.map((u) => (u.id === id ? { ...u, ...mergedUpdates } : u));
                    set({ users: updated });
                    if (user?.id === id) {
                        set({ user: { ...user, ...mergedUpdates } });
                    }
                    return true;
                }
            }
            catch {
                // Server unavailable — fall back to local update
            }
        }
        const updated = users.map((u) => (u.id === id ? { ...u, ...updates } : u));
        set({ users: updated });
        if (user?.id === id) {
            set({ user: { ...user, ...updates } });
        }
        return true;
    },
    toggleUserActive: async (id) => {
        const { users, serverUrl } = get();
        if (serverUrl) {
            try {
                const client = get().getClient();
                if (client) {
                    const authApi = createAuthApi(client);
                    await authApi.toggleUserActive(id);
                }
            }
            catch {
                // Server unavailable — still toggle locally
            }
        }
        set({
            users: users.map((u) => (u.id === id ? { ...u, active: !u.active } : u)),
        });
    },
    getClient: () => {
        const { serverUrl, accessToken, refreshToken } = get();
        if (!serverUrl)
            return null;
        const client = new ApiClient(serverUrl);
        if (accessToken && refreshToken) {
            client.setTokens(accessToken, refreshToken);
            client.setOnRefreshFailed(() => {
                set({
                    user: null,
                    isAuthenticated: false,
                    accessToken: null,
                    refreshToken: null,
                    error: 'Session expired. Please log in again.',
                });
            });
        }
        return client;
    },
}), {
    name: 'projectmanmd-auth',
    onRehydrateStorage: () => {
        return (state) => {
            if (!state)
                return;
            if (state.serverUrl && !state.accessToken) {
                state.isAuthenticated = false;
                state.user = null;
            }
            else if (state.user) {
                state.isAuthenticated = true;
            }
        };
    },
    partialize: (state) => ({
        user: state.user,
        users: state.users,
        serverUrl: state.serverUrl,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
    }),
}));
//# sourceMappingURL=auth-store.js.map